/**
 * CARTO basemap tile helpers.
 *
 * Since 2026-09-25 CARTO refuses to serve raster tiles without a key: instead of
 * failing, it returns an "API KEY REQUIRED" watermark image, which Leaflet happily
 * renders as if it were the map.
 *
 * The key travels in the tile URL, so it is public by design — it ends up in the
 * bundle and in every tile request. Restrict it to this app's domains in the CARTO
 * dashboard rather than treating it as a secret.
 */

// Trimmed on purpose: a key pasted into a dashboard env var easily carries a
// trailing newline, which encodeURIComponent turns into "%0A" — CARTO then treats
// the key as invalid and silently serves the watermark instead of failing loudly.
const CARTO_KEY = (import.meta.env.VITE_CARTO_API_KEY || '').trim();

if (!CARTO_KEY) {
  console.error(
    '[Map] VITE_CARTO_API_KEY is missing — CARTO will serve "API KEY REQUIRED" ' +
      'watermarked tiles. Get a free key at https://carto.com/basemaps/apikey'
  );
}

/**
 * Appends the CARTO API key to a basemap tile URL template.
 *
 * Leaflet's `{s}` / `{z}` / `{x}` / `{y}` placeholders are left untouched — it
 * substitutes them without caring about the query string.
 *
 * @param url Tile URL template under basemaps.cartocdn.com
 * @returns The template with `?key=...` appended, or unchanged when no key is set
 */
export function withCartoKey(url: string): string {
  return CARTO_KEY ? `${url}?key=${encodeURIComponent(CARTO_KEY)}` : url;
}
